import _ from 'lodash';
import { ENCAISSEMENT, MENUS } from '@/constants';

/**
 * Mode de lancement de l'application en accès direct (URL propre, hors shell 360).
 *
 * - **normal** (défaut) : la régie complète.
 * - **encaissement direct** (LOOC) : l'onglet est cloisonné à `/encaissement-direct`.
 *
 * Le mode est décidé une seule fois au chargement, à partir des paramètres posés par
 * api-regie (`mode=encaissement-direct`, ou ancien format `identifiantRegie` +
 * `identifiantRegisseur`), puis mémorisé en sessionStorage. Il n'est jamais rétrogradé
 * dans l'onglet : retirer les paramètres de l'URL et recharger ne fait pas sortir de
 * l'encaissement direct. Seul un nouveau lien à jeton (`TOKEN` différent) repart d'une
 * session vierge.
 */

const STORAGE_KEY = 'regieEmbedded';
const JETON_KEY = 'regieJeton';

interface InfosEncaissementDirectType {
  identifiantRegie: number;
  identifiantRegisseur: number;
  identifiantFacture: number;
  done?: boolean;
}

/** L'application est montée dans le shell 360 (même test que `render` de `@mairistem/module`). */
export const estMonteeDansShell = (): boolean => !!document.querySelector('#module');

export const getInfosEncaissementDirect = (): InfosEncaissementDirectType | null => {
  const brut = sessionStorage.getItem(STORAGE_KEY);

  if (!brut) return null;

  try {
    return JSON.parse(brut);
  } catch {
    return null;
  }
};

export const estEncaissementDirect = (): boolean => !_.isNil(getInfosEncaissementDirect());

export const estEncaissementTermine = (): boolean => getInfosEncaissementDirect()?.done === true;

export const marquerEncaissementTermine = (done: boolean): void => {
  const infos = getInfosEncaissementDirect();

  if (infos) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...infos, done }));
  }
};

/**
 * Détermine le mode de lancement à partir de l'URL. Doit s'exécuter avant tout autre
 * module de l'application (stores zustand persistés en sessionStorage compris) et avant
 * la création du routeur du shell de @mairistem/module.
 */
export const initialiserModeLancement = (search: string = window.location.search): void => {
  if (estMonteeDansShell()) return;

  const params = new URLSearchParams(search);
  const jeton = params.get('TOKEN');
  const jetonMemorise = sessionStorage.getItem(JETON_KEY);

  if (!_.isNil(jeton) && jeton !== jetonMemorise) {
    // Nouveau lien à jeton : on repart d'une session vierge pour que @mairistem/module
    // se ré-authentifie et que la régie / le régisseur du lien soient repris.
    // Exception : onglet déjà authentifié avant la mise en place de ce mécanisme (aucun
    // jeton mémorisé) — on se contente de mémoriser le jeton pour ne pas rejouer l'auth.
    if (!_.isNil(jetonMemorise) || _.isNil(sessionStorage.getItem('token'))) {
      sessionStorage.clear();
    }
    sessionStorage.setItem(JETON_KEY, jeton);
  }

  const identifiantRegie = params.get('identifiantRegie');
  const identifiantRegisseur = params.get('identifiantRegisseur');
  const demandeEncaissementDirect =
    params.get('mode') === ENCAISSEMENT.MODE_ENCAISSEMENT_DIRECT ||
    (!_.isNil(identifiantRegie) && !_.isNil(identifiantRegisseur));

  // On n'écrase pas un encaissement direct déjà initialisé (rechargement) pour conserver `done`.
  if (demandeEncaissementDirect && !estEncaissementDirect()) {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        identifiantRegie: Number(identifiantRegie),
        identifiantRegisseur: Number(identifiantRegisseur),
        identifiantFacture: Number(params.get('identifiantFacture')),
      }),
    );
  }

  // En encaissement direct, seule la feature /encaissement-direct est déclarée : on y place
  // l'URL avant la création du routeur du shell, sinon aucune feature ne correspond (lien
  // LOOC à la racine, rechargement sur une autre route…) et rien n'est monté.
  // La query est conservée : @mairistem/module y lit TOKEN / infoSup pour l'authentification.
  const pathEncaissementDirect = MENUS.LOCATIONS[MENUS.MENU_ENCAISSEMENT_DIRECT];

  if (estEncaissementDirect() && window.location.pathname !== pathEncaissementDirect) {
    window.history.replaceState(
      window.history.state,
      '',
      `${pathEncaissementDirect}${window.location.search}${window.location.hash}`,
    );
  }
};
